import React, { useEffect, useState } from 'react';
import { Settings as SettingsIcon, User, Bell, Shield, Palette, Save } from 'lucide-react';
import './Settings.css';

const AdminSettings = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [settings, setSettings] = useState({
    profile: {
      username: '',
      email: '',
      role: ''
    },
    notifications: {
      emailNotifications: true,
      pushNotifications: true,
      vendorUpdates: true,
      systemAlerts: true,
      weeklyReports: false
    },
    security: {
      twoFactorAuth: false,
      sessionTimeout: '30',
      passwordExpiry: '90',
      loginAlerts: true
    },
    appearance: {
      theme: 'light',
      sidebarCollapsed: false,
      compactMode: false
    }
  });

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'appearance', label: 'Appearance', icon: Palette }
  ];

  const handleSettingChange = (category, key, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value
      }
    }));
  };

  const loadAdminProfile = async () => {
    try {
      const apiKey = localStorage.getItem('adminApiKey') || 'dev-admin-key';
      const res = await fetch('/api/admin/profile', {
        headers: { 'x-admin-key': apiKey }
      });
      if (res.ok) {
        const data = await res.json();
        setSettings(prev => ({
          ...prev,
          profile: {
            username: data.username || '',
            email: data.email || '',
            role: data.role || ''
          }
        }));
      }
    } catch (e) {
      console.error('Load admin profile error:', e);
    }
  };

  useEffect(() => { 
    loadAdminProfile(); 
  }, []);

  const handleSave = async () => {
    try {
      const apiKey = localStorage.getItem('adminApiKey') || 'dev-admin-key';
      const res = await fetch('/api/admin/profile', {
        method: 'PUT',
        headers: { 
          'x-admin-key': apiKey, 
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify({
          username: settings.profile.username,
          email: settings.profile.email
        })
      });
      if (!res.ok) {
        const e = await res.json();
        alert(e.error || 'Failed to save profile');
      } else {
        const data = await res.json();
        alert('Profile updated successfully!');
        await loadAdminProfile();
      }
    } catch (e) { 
      console.error('Save profile error:', e);
      alert('Failed to save profile. Please try again.');
    }
  };

  const renderProfileSettings = () => (
    <div className="settings-section">
      <h3>Admin Profile</h3>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Username</label>
          <input
            type="text"
            className="form-input"
            value={settings.profile.username}
            onChange={(e) => handleSettingChange('profile', 'username', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Email</label>
          <input
            type="email"
            className="form-input"
            value={settings.profile.email}
            onChange={(e) => handleSettingChange('profile', 'email', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Role</label>
          <input
            type="text"
            className="form-input"
            value={settings.profile.role}
            disabled
            style={{ backgroundColor: '#f5f5f5', color: '#666' }}
          />
          <small style={{ color: '#666', fontSize: '12px' }}>Role cannot be changed</small>
        </div>
      </div>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="settings-section">
      <h3>Notification Preferences</h3>
      <div className="settings-list">
        <div className="setting-item">
          <div className="setting-info">
            <h4>Email Notifications</h4>
            <p>Receive important updates via email</p>
          </div>
          <label className="toggle">
            <input
              type="checkbox"
              checked={settings.notifications.emailNotifications}
              onChange={(e) => handleSettingChange('notifications', 'emailNotifications', e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>
        <div className="setting-item">
          <div className="setting-info">
            <h4>Push Notifications</h4>
            <p>Receive real-time notifications</p>
          </div>
          <label className="toggle">
            <input
              type="checkbox"
              checked={settings.notifications.pushNotifications}
              onChange={(e) => handleSettingChange('notifications', 'pushNotifications', e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>
        <div className="setting-item">
          <div className="setting-info">
            <h4>Vendor Updates</h4>
            <p>Get notified about vendor activities</p>
          </div>
          <label className="toggle">
            <input
              type="checkbox"
              checked={settings.notifications.vendorUpdates}
              onChange={(e) => handleSettingChange('notifications', 'vendorUpdates', e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>
        <div className="setting-item">
          <div className="setting-info">
            <h4>System Alerts</h4>
            <p>Receive critical system notifications</p>
          </div>
          <label className="toggle">
            <input
              type="checkbox"
              checked={settings.notifications.systemAlerts}
              onChange={(e) => handleSettingChange('notifications', 'systemAlerts', e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>
        <div className="setting-item">
          <div className="setting-info">
            <h4>Weekly Reports</h4>
            <p>Receive weekly platform reports</p>
          </div>
          <label className="toggle">
            <input
              type="checkbox"
              checked={settings.notifications.weeklyReports}
              onChange={(e) => handleSettingChange('notifications', 'weeklyReports', e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>
      </div>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="settings-section">
      <h3>Security Settings</h3>
      <div className="settings-list">
        <div className="setting-item">
          <div className="setting-info">
            <h4>Two-Factor Authentication</h4>
            <p>Add an extra layer of security to your account</p>
          </div>
          <label className="toggle">
            <input
              type="checkbox"
              checked={settings.security.twoFactorAuth}
              onChange={(e) => handleSettingChange('security', 'twoFactorAuth', e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>
        <div className="setting-item">
          <div className="setting-info">
            <h4>Session Timeout</h4>
            <p>Automatically log out after inactivity</p>
          </div>
          <select
            className="form-select"
            value={settings.security.sessionTimeout}
            onChange={(e) => handleSettingChange('security', 'sessionTimeout', e.target.value)}
          >
            <option value="15">15 minutes</option>
            <option value="30">30 minutes</option>
            <option value="60">1 hour</option>
            <option value="120">2 hours</option>
          </select>
        </div>
        <div className="setting-item">
          <div className="setting-info">
            <h4>Password Expiry</h4>
            <p>Require password change after specified days</p>
          </div>
          <select
            className="form-select"
            value={settings.security.passwordExpiry}
            onChange={(e) => handleSettingChange('security', 'passwordExpiry', e.target.value)}
          >
            <option value="30">30 days</option>
            <option value="60">60 days</option>
            <option value="90">90 days</option>
            <option value="180">180 days</option>
            <option value="365">1 year</option>
          </select>
        </div>
        <div className="setting-item">
          <div className="setting-info">
            <h4>Login Alerts</h4>
            <p>Get notified of new login attempts</p>
          </div>
          <label className="toggle">
            <input
              type="checkbox"
              checked={settings.security.loginAlerts}
              onChange={(e) => handleSettingChange('security', 'loginAlerts', e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>
      </div>
    </div>
  );

  const renderAppearanceSettings = () => (
    <div className="settings-section">
      <h3>Appearance Settings</h3>
      <div className="settings-list">
        <div className="setting-item">
          <div className="setting-info">
            <h4>Theme</h4>
            <p>Choose your preferred color scheme</p>
          </div>
          <select
            className="form-select"
            value={settings.appearance.theme}
            onChange={(e) => handleSettingChange('appearance', 'theme', e.target.value)}
          >
            <option value="light">Light</option>
            <option value="dark">Dark</option>
            <option value="auto">Auto</option>
          </select>
        </div>
        <div className="setting-item">
          <div className="setting-info">
            <h4>Sidebar Collapsed</h4>
            <p>Start with sidebar collapsed by default</p>
          </div>
          <label className="toggle">
            <input
              type="checkbox"
              checked={settings.appearance.sidebarCollapsed}
              onChange={(e) => handleSettingChange('appearance', 'sidebarCollapsed', e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>
        <div className="setting-item">
          <div className="setting-info">
            <h4>Compact Mode</h4>
            <p>Use compact spacing for better screen utilization</p>
          </div>
          <label className="toggle">
            <input
              type="checkbox"
              checked={settings.appearance.compactMode}
              onChange={(e) => handleSettingChange('appearance', 'compactMode', e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'profile':
        return renderProfileSettings();
      case 'notifications':
        return renderNotificationSettings();
      case 'security':
        return renderSecuritySettings();
      case 'appearance':
        return renderAppearanceSettings();
      default:
        return renderProfileSettings();
    }
  };

  return (
    <div className="settings">
      <div className="settings-header">
        <div className="header-content">
          <h1>Admin Settings</h1>
          <p>Manage your admin profile, preferences, and appearance</p>
        </div>
        <button className="btn btn-primary" onClick={handleSave}>
          <Save size={20} />
          Save Changes
        </button>
      </div>

      <div className="settings-container">
        <div className="settings-sidebar">
          <nav className="settings-nav">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  className={`nav-item ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <Icon size={20} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="settings-content">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
